import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Calendar, Check, AlertTriangle, ArrowRight, Wrench, PoundSterling } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { getWhatsAppUrl, trackEvent } from "@/lib/tracking";
import { FAQAccordion } from "@/components/FAQAccordion";
import { DriverSupportCTA } from "@/components/DriverSupportCTA";
import type { CostPageContent } from "@/lib/cost-pages";

interface Props {
  content: CostPageContent;
}

/**
 * Shared layout for /cost/* money pages. Money page = high-intent commercial
 * search ("how much does X cost in East London?") that converts via call,
 * WhatsApp or booking. No fixed prices are ever rendered — we always link the
 * customer to a quote channel and explain the cost drivers honestly.
 */
export function CostPageTemplate({ content }: Props) {
  const biz = getBusinessConfig();
  const wa = getWhatsAppUrl(content.service, {
    area: "East London",
    notes: `Cost guide enquiry: ${content.service} (guide: ${content.slug})`,
    pageType: "cost",
  });
  const trackMeta = { service: content.serviceSlug, page_type: "cost", guide: content.slug } as const;

  return (
    <article className="bg-background">
      {/* HERO */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="mb-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span className="mx-1.5">/</span>
            <Link to="/cost" className="hover:text-foreground">Cost guides</Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{content.service}</span>
          </nav>
          <p className="eyebrow text-primary">
            <PoundSterling className="inline h-3.5 w-3.5 mr-1" />
            Honest indicative pricing — no booking fees
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold text-foreground leading-tight">
            {content.h1}
          </h1>
          <div className="mt-4 space-y-3 text-base md:text-lg text-muted-foreground max-w-3xl">
            {content.intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl">
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { location: "cost_hero", ...trackMeta })}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              <Phone className="h-4 w-4" /> Call
            </a>
            <a
              href={wa}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "cost_hero", ...trackMeta })}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Link
              to="/book"
              search={{ service: content.serviceSlug }}
              onClick={() => trackEvent("service_cta_click", { location: "cost_hero", ...trackMeta })}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-3 text-sm font-bold text-background hover:opacity-90"
            >
              <Calendar className="h-4 w-4" /> Book
            </Link>
            <Link
              to="/contact"
              onClick={() => trackEvent("service_cta_click", { location: "cost_hero_quote", ...trackMeta })}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-3 text-sm font-bold text-foreground hover:border-primary/40"
            >
              <Wrench className="h-4 w-4" /> Quote
            </Link>
          </div>
        </div>
      </header>

      {/* CALLOUT */}
      <section className="mx-auto max-w-4xl px-4 py-8">
        <div className="rounded-2xl border-l-4 border-primary bg-primary/5 p-5 md:p-6">
          <p className="text-base md:text-lg font-semibold text-foreground">{content.callout}</p>
        </div>
      </section>

      {/* WHAT AFFECTS THE COST */}
      <section className="mx-auto max-w-4xl px-4 py-6">
        <h2 className="text-2xl font-bold text-foreground">What affects the cost</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          We won't pretend to fix a price online — every car is different. These are the honest variables that decide what you pay.
        </p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {content.factors.map((f) => (
            <div key={f.name} className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-bold text-foreground">{f.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="mx-auto max-w-4xl px-4 py-8">
        <h2 className="text-2xl font-bold text-foreground">What's included</h2>
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          {content.whatsIncluded.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-foreground">
              <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* RED FLAGS */}
      {content.redFlags && content.redFlags.length > 0 && (
        <section className="mx-auto max-w-4xl px-4 py-6">
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Warning signs — don't ignore these
            </h2>
            <ul className="mt-3 space-y-2">
              {content.redFlags.map((flag) => (
                <li key={flag} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                  <span>{flag}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* DEDICATED QUOTE CTA — pre-filled with this guide's service context */}
      <section className="mx-auto max-w-4xl px-4 py-8">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 md:p-8">
          <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="eyebrow text-primary">
                <PoundSterling className="inline h-3.5 w-3.5 mr-1" />
                Get a quote for {content.service.toLowerCase()}
              </p>
              <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground">
                Tell us the reg — we'll quote your exact price in under a minute
              </h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground">
                Tap call or WhatsApp below and the message is already filled in with the right service
                ({content.service}) and area (East London). No booking fees, no upsell.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[220px]">
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "cost_quote_block", ...trackMeta })}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
              >
                <Phone className="h-4 w-4" /> Call {biz.phone}
              </a>
              <a
                href={wa}
                target="_blank" rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "cost_quote_block", ...trackMeta })}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <Link
                to="/contact"
                onClick={() => trackEvent("service_cta_click", { location: "cost_quote_block", ...trackMeta })}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40"
              >
                <Wrench className="h-4 w-4" /> Send details
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LONG-FORM COPY */}
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h2 className="text-2xl font-bold text-foreground">Honest detail — what the numbers actually mean</h2>
        <div className="mt-4 space-y-4 text-base text-foreground/90 leading-relaxed">
          {content.longCopy.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-8">
        <h2 className="text-2xl font-bold text-foreground">Common questions</h2>
        <div className="mt-4">
          <FAQAccordion faqs={content.faqs} />
        </div>
      </section>

      {/* DRIVER SUPPORT — PCO/private hire/value cross-link */}
      <section className="mx-auto max-w-4xl px-4">
        <DriverSupportCTA page={`cost/${content.slug}`} service={content.serviceSlug} bookingContext={`cost-${content.slug}`} />
      </section>

      {/* RELATED LINKS */}
      <section className="mx-auto max-w-4xl px-4 py-8">
        <h2 className="text-xl font-bold text-foreground">Related guides &amp; services</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {content.relatedServices.map((r) => (
            <a
              key={r.href}
              href={r.href}
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/50"
            >
              <span className="text-sm font-semibold text-foreground">{r.label}</span>
              <ArrowRight className="h-4 w-4 text-primary" />
            </a>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            Get a real quote in under a minute
          </h2>
          <p className="mt-2 text-sm md:text-base text-muted-foreground">
            Drop us the vehicle reg on {biz.phone} or via WhatsApp and we'll quote the exact price for your car — no booking fees, no upsell.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { location: "cost_final_cta", ...trackMeta })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              <Phone className="h-4 w-4" /> {biz.phone}
            </a>
            <a
              href={wa}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "cost_final_cta", ...trackMeta })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp the workshop
            </a>
          </div>
        </div>
      </section>
    </article>
  );
}
